import type {APITask} from '@github-ui/agent-sessions/types/api-task'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {issueAgentTasksPath} from '@github-ui/paths'
import {useQuery} from '@github-ui/react-query'
import {reactFetchJSON} from '@github-ui/verified-fetch'

export type IssueAgentTask = APITask & {
  url?: string
}

export type UseIssueAgentTasksParams = {
  owner: string
  repo: string
  issueNumber: number
}

/**
 * Fetches agent tasks for a given issue via the REST API.
 */
export function useIssueAgentTasks({owner, repo, issueNumber}: UseIssueAgentTasksParams) {
  const isIssueCCAVisualizationEnabled = isFeatureEnabled('issue_cca_visualization')
  const enabled =
    isIssueCCAVisualizationEnabled &&
    owner.length > 0 &&
    repo.length > 0 &&
    Number.isInteger(issueNumber) &&
    issueNumber > 0

  return useQuery<{tasks: IssueAgentTask[]}>({
    enabled,
    queryKey: ['issue-agent-tasks', owner, repo, issueNumber],
    queryFn: async () => {
      const response = await reactFetchJSON(issueAgentTasksPath({owner, repo, issueNumber}))

      if (!response.ok) {
        throw new Error(`Failed to fetch agent tasks: ${response.status}`)
      }

      return (await response.json()) as {tasks: IssueAgentTask[]}
    },
    retry: false,
  })
}
